// app.config.ts
import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  ErrorHandler,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { GlobalErrorHandler } from './shared/services/global-error-handler.service';

import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';

import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
  LogLevel,
} from '@azure/msal-browser';

import { environment } from '../environments/environment';

function loggerCallback(_logLevel: LogLevel, message: string) {
  console.log('[MSAL]', message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.b2c.clientId,
      authority: environment.b2c.authorities.signUpSignIn.authority,
      knownAuthorities: [environment.b2c.authorityDomain],
      redirectUri: environment.b2c.redirectUri,
      postLogoutRedirectUri: environment.b2c.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.b2c.apiScopes,
    },
    loginFailedRoute: '/auth/login',
  };
}


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  const apiBase = environment.baseUrl.replace(/\/$/, '');
  const apiBaseWithSlash = `${apiBase}/`;

  protectedResourceMap.set(apiBase, environment.b2c.apiScopes);
  protectedResourceMap.set(apiBaseWithSlash, environment.b2c.apiScopes);

  const apiOrigin = new URL(apiBase).origin;
  protectedResourceMap.set(apiOrigin, environment.b2c.apiScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Inicializa MSAL antes de que Angular cree servicios/componentes.
 * (equivalente a APP_INITIALIZER, pero usando provideAppInitializer)
 */
async function initMsal(msalService: MsalService) {
  await msalService.instance.initialize();

  try {
    const result = await msalService.instance.handleRedirectPromise();

    if (result?.account) {
      msalService.instance.setActiveAccount(result.account);
      return;
    }

    const accounts = msalService.instance.getAllAccounts();
    if (!msalService.instance.getActiveAccount() && accounts.length > 0) {
      msalService.instance.setActiveAccount(accounts[0]);
    }
  } catch (error: any) {
    console.error('[MSAL] Error in handleRedirectPromise during initialization:', error);
    // Si el usuario cancela un flujo (ej: recuperación de contraseña AADB2C90091)
    // o ocurre otro error en el redirect, redirigimos a la página de inicio de sesión
    window.location.href = '/auth/login';
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },

    // HTTP: Interceptores en orden:
    // 1. authInterceptor: Agrega token de localStorage a todas las peticiones
    // 2. errorInterceptor: Maneja errores HTTP (debe ser el último)
    // NOTA: Se removió msalOptionalAuthInterceptor porque estaba causando conflictos.
    // El authInterceptor maneja correctamente el token de localStorage que el backend requiere.
    provideHttpClient(
      withInterceptors([authInterceptor, errorInterceptor])
    ),

    provideRouter(routes),

    // MSAL providers (factory)
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },

    // Servicios MSAL
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => initMsal(inject(MsalService))),
  ],
};
